import './Menu.css';

import React, { type FC, useEffect, useState } from 'react';

import { useUnmountReactAndNavigate } from '@src/hooks';

import { useConfigInfo } from '@src/contexts';
import { Pages } from '@src/Pages';

import { getOnboardingStatus } from '@vse/api/machinemotion.js';
import { checkIfHasFullAccess } from '@src/Main/main';

import { type PasswordTarget } from '@src/ConfigInfo';
import { PasswordBadge } from './PasswordBadge';
import { ConnectivityStatus } from './ConnectivityStatus';
import { MenuNavigationButtons } from './MenuNavigationButtons';
import { RemoteSupportEntryButton } from '@ventionco/remote-support-components';
import { remoteSupportServices } from '@src/RemoteSupportServices';
import { EnvironmentVariables } from '@src/EnvironmentVariables';

function hasDefaultPasswords (data: PasswordTarget[] | undefined): boolean {
  if (data === undefined) { return false; }
  return data.some(
    machines => {
      if (machines.defaultTargets === undefined) { return false; }
      return Object.values(machines.defaultTargets).some(isDefault => isDefault);
    },
  );
}

interface MenuProps {
  isPendantAvailable: boolean
  isMachineAppAvailable: boolean
}

export const Menu: FC<MenuProps> = ({ isPendantAvailable, isMachineAppAvailable }) => {
  const configInfoContext = useConfigInfo();
  const [hasFullAccess, setHasFullAccess] = useState(false);
  const navigate = useUnmountReactAndNavigate();

  useEffect(() => {
    if (EnvironmentVariables.CI_RUN) {
      return;
    }

    void getOnboardingStatus(onboardingState => {
      let shouldShowOnboarding = false;

      if (onboardingState?.disclaimerRead === false || (onboardingState?.onboarded === false && isPendantAvailable)) {
        shouldShowOnboarding = true;
      }

      if (shouldShowOnboarding) { navigate(Pages.ONBOARDING); }
    });
  }, []);

  useEffect(() => {
    checkIfHasFullAccess(setHasFullAccess);
  }, []);

  const newTargets = hasDefaultPasswords(configInfoContext.configInfo?.passwordTargets);

  return (
    <div id='menu-container'>
      <div id='menu-navbar'>
        <RemoteSupportEntryButton
          onClick={() => { navigate(Pages.REMOTE_SUPPORT); }}
          isInCall={remoteSupportServices.isInCall()}
          isInWaitingRoom={remoteSupportServices.isInWaitingRoom()}
        />
        <ConnectivityStatus/>
      </div>
      <h2 className='menu-headline'> Control Center </h2>
      <MenuNavigationButtons restrictAccess={!hasFullAccess} isMachineAppAvailable={isMachineAppAvailable}/>
      { newTargets && <PasswordBadge onClick={() => { navigate({ pathname: Pages.ONBOARDING, search: '?showPasswordView=true' }); }}/> }
    </div>
  );
};
